import {
	getAuthIdentity,
	onAuthIdentityChange,
	setAuthTokenProvider,
} from '@/doc-sync/auth-identity';
import type { AuthIdentity } from '@/doc-sync/auth-identity';
import { makeOwnershipPrepare } from '@/doc-sync/ownership-prepare';
import { wipeAllOwnedCaches } from '@/persistence/owned-caches';

import type { DiagramStore } from './diagram-store';
import { IndexedDbDiagramStore, isIndexedDbAvailable } from './indexeddb-diagram-store';
import { RemoteDiagramStore } from './remote-diagram-store';
import { SyncedDiagramStore } from './synced-diagram-store';

export { getAuthIdentity, setAuthTokenProvider };
export type { AuthIdentity };

let localInstance: (IndexedDbDiagramStore & DiagramStore) | null | undefined;
let syncedInstance: SyncedDiagramStore | null = null;
/** Test-only stand-in for the whole store (see setDiagramStoreForTests). */
let testInstance: DiagramStore | null | undefined;

// Rebuild the synced store lazily whenever the signed-in identity changes
// (including to/from null) — the registry now lives in doc-sync so pattern
// persistence (PR C) can share one registration from InnerApp.
onAuthIdentityChange(() => {
	syncedInstance = null;
});

/** Lazy app-wide store. Null when IndexedDB is unavailable (private browsing,
 *  ancient browsers) — callers degrade: editor keeps working with a
 *  "changes won't be saved" banner; gallery shows the unavailable state.
 *  Synced (local + remote) once an identity is registered via
 *  setAuthTokenProvider; local-only otherwise. */
export function getDiagramStore(): DiagramStore | null {
	if (testInstance !== undefined) return testInstance;
	if (localInstance === undefined) {
		localInstance = isIndexedDbAvailable() ? new IndexedDbDiagramStore() : null;
	}
	if (localInstance == null) return null;
	const identity = getAuthIdentity();
	if (identity == null) return localInstance;
	if (syncedInstance == null) {
		const local = localInstance;
		// The provider ref is captured through a closure that always reads the
		// CURRENT registry identity, so token refreshes/rotations flow through.
		syncedInstance = new SyncedDiagramStore({
			local,
			remote: new RemoteDiagramStore({
				getToken: () => {
					const id = getAuthIdentity();
					return id ? id.getToken() : Promise.resolve(null);
				},
			}),
			prepare: makeOwnershipPrepare(identity.userId, () => wipeAllOwnedCaches()),
		});
	}
	return syncedInstance;
}

/** Test-only: clears all module-level singleton state. Needed because bun
 *  test otherwise leaks this singleton across test files. */
export function resetDiagramStoreForTests(): void {
	setAuthTokenProvider(null);
	localInstance = undefined;
	syncedInstance = null;
	testInstance = undefined;
}

/** Test-only: makes getDiagramStore() return `store` (e.g. a SyncedDiagramStore
 *  over in-memory fakes) until resetDiagramStoreForTests(), so a page test can
 *  run the real galleries against a signed-in-shaped store without a network. */
export function setDiagramStoreForTests(store: DiagramStore | null): void {
	testInstance = store;
}
